// ─── ActivitySessionManager — orchestrates a walking session ───────────────────
import type { ActivityStats, ActivityStatus, GpsMode, GpsPoint, ProcessedPoint, RouteQuality } from "./types";
import { LocationService, type GpsDiagnostics } from "./LocationService";
import { RouteProcessor } from "./RouteProcessor";
import { StepService, calcCalories, calcAvgPace, requestMotionPermission } from "./Calculators";

const STATIONARY_SPEED_MPS = 0.3;
const AUTO_PAUSE_DELAY_MS = 10000;
const AUTO_RESUME_SPEED_MPS = 0.5;

type StatsCallback = (stats: ActivityStats) => void;
type PointCallback = (points: GpsPoint[]) => void;
type DiagCallback = (diag: GpsDiagnostics) => void;

export class ActivitySessionManager {
  private location = new LocationService();
  private route = new RouteProcessor();
  private steps = new StepService();
  private status: ActivityStatus = "idle";
  private startTs = 0;
  private movingSecs = 0;
  private pausedSecs = 0;
  private lastUpdateTs = 0;
  private maxSpeed = 0;
  private autoPauseTimer: ReturnType<typeof setTimeout> | null = null;
  private statsCallback: StatsCallback | null = null;
  private pointCallback: PointCallback | null = null;
  private diagCallback: DiagCallback | null = null;
  private weightKg = 70;
  private wakeLock: WakeLockSentinel | null = null;
  private tickTimer: ReturnType<typeof setInterval> | null = null;

  onStart(onStats: StatsCallback, onPoints: PointCallback, onDiag?: DiagCallback) {
    this.statsCallback = onStats;
    this.pointCallback = onPoints;
    this.diagCallback = onDiag ?? null;
  }

  async start(weightKg: number, mode: GpsMode): Promise<boolean> {
    this.weightKg = weightKg;
    this.route.reset();
    this.startTs = Date.now();
    this.movingSecs = 0;
    this.pausedSecs = 0;
    this.maxSpeed = 0;
    this.status = "tracking";

    // Motion permission is non-blocking — GPS tracking works without it.
    // If motion is denied, step counting won't work but the walk still tracks.
    requestMotionPermission().then((granted) => {
      if (granted) {
        this.steps.start((_s) => { this.onStep(); }).catch(() => {});
      }
    });

    this.location.setMode(mode);
    if (this.diagCallback) this.location.onDiagnostics(this.diagCallback);
    this.location.start((point) => {
      if (point) this.onGpsPoint(point);
    });

    // Request wake lock for background tracking
    try {
      if ("wakeLock" in navigator) {
        this.wakeLock = await navigator.wakeLock.request("screen");
      }
    } catch {}

    // Tick every second to update elapsed time
    this.tickTimer = setInterval(() => this.tick(), 1000);

    this.emitStats();
    return true;
  }

  pause() {
    if (this.status !== "tracking") return;
    this.status = "paused";
    if (this.autoPauseTimer) { clearTimeout(this.autoPauseTimer); this.autoPauseTimer = null; }
    this.emitStats();
  }

  resume() {
    if (this.status !== "paused") return;
    this.status = "tracking";
    this.emitStats();
  }

  finish(): { stats: ActivityStats; points: GpsPoint[] } {
    this.status = "finished";
    this.location.stop();
    this.steps.stop();
    if (this.autoPauseTimer) { clearTimeout(this.autoPauseTimer); this.autoPauseTimer = null; }
    if (this.tickTimer) { clearInterval(this.tickTimer); this.tickTimer = null; }
    this.releaseWakeLock();
    const stats = this.computeStats();
    const points = this.route.getPoints().map(p => ({
      lat: p.lat, lng: p.lng, ts: p.ts, accuracy: p.accuracy,
      speed: p.speed, heading: p.heading, altitude: p.altitude, moving: p.moving,
    }));
    return { stats, points };
  }

  stop() {
    this.location.stop();
    this.steps.stop();
    if (this.autoPauseTimer) { clearTimeout(this.autoPauseTimer); this.autoPauseTimer = null; }
    if (this.tickTimer) { clearInterval(this.tickTimer); this.tickTimer = null; }
    this.releaseWakeLock();
    this.status = "idle";
  }

  setMode(mode: GpsMode) {
    this.location.setMode(mode);
  }

  getStatus() { return this.status; }

  private onGpsPoint(point: GpsPoint) {
    const processed = this.route.addPoint(point);
    if (!processed) return;

    // Compute speed from GPS distance if the native speed is 0/null
    const effectiveSpeed = point.speed > 0 ? point.speed : this.computeGpsSpeed(processed);
    if (effectiveSpeed > this.maxSpeed) this.maxSpeed = effectiveSpeed;

    // Auto pause/resume — only when GPS clearly shows no movement for a while
    if (this.status === "tracking" && effectiveSpeed < STATIONARY_SPEED_MPS) {
      if (!this.autoPauseTimer) {
        this.autoPauseTimer = setTimeout(() => {
          if (this.status === "tracking") this.pause();
        }, AUTO_PAUSE_DELAY_MS);
      }
    } else if (this.status === "paused" && effectiveSpeed > AUTO_RESUME_SPEED_MPS) {
      this.resume();
    }

    if (this.status === "tracking" && point.moving) {
    }

    if (this.pointCallback) {
      this.pointCallback(this.route.getPoints().map(p => ({
        lat: p.lat, lng: p.lng, ts: p.ts, accuracy: p.accuracy,
        speed: p.speed, heading: p.heading, altitude: p.altitude, moving: p.moving,
      })));
    }

    this.emitStats();
  }

  private onStep() {
    if (this.status !== "tracking") return;
    if (this.autoPauseTimer) { clearTimeout(this.autoPauseTimer); this.autoPauseTimer = null; }
  }

  private tick() {
    if (this.status === "tracking") {
      const now = Date.now();
      const dt = (now - (this.lastUpdateTs || this.startTs)) / 1000;
      // Count time as moving whenever the session is actively tracking
      // (GPS is providing fixes). This avoids the step counter being a
      // hard dependency for time/calorie tracking.
      this.movingSecs += dt;
    }
    this.emitStats();
  }

  private computeStats(): ActivityStats {
    const distance = this.route.getDistance();
    const stepCount = this.steps.getSteps();
    const calories = calcCalories(this.weightKg, distance, this.movingSecs, this.route.getElevationGain());
    const avgSpeed = this.movingSecs > 0 ? distance / this.movingSecs : 0;
    const avgPace = calcAvgPace(distance, this.movingSecs);
    const points = this.route.getPoints();
    const currentPoint = points.length > 0 ? points[points.length - 1] : null;
    const currentSpeed = currentPoint?.speed ?? 0;
    // Use computed GPS speed if native speed is unavailable
    const effectiveCurrentSpeed = currentSpeed > 0 ? currentSpeed : this.computeGpsSpeed(currentPoint);
    const currentPace = currentSpeed > 0.1 ? 1 / (currentSpeed / 1000) : 0;
    const routeQuality: RouteQuality = this.route.getRouteQuality();

    return {
      distance,
      steps: stepCount,
      calories,
      movingSecs: Math.round(this.movingSecs),
      pausedSecs: Math.round(this.pausedSecs),
      totalSecs: Math.round((Date.now() - this.startTs) / 1000),
      avgSpeed,
      currentSpeed: effectiveCurrentSpeed,
      maxSpeed: this.maxSpeed,
      avgPace,
      currentPace,
      elevationGain: this.route.getElevationGain(),
      elevationLoss: this.route.getElevationLoss(),
      minElevation: this.route.getMinElevation(),
      maxElevation: this.route.getMaxElevation(),
      currentElevation: currentPoint?.altitude ?? null,
      gpsAccuracy: currentPoint?.accuracy ?? 0,
      avgGpsAccuracy: this.route.getAvgAccuracy(),
      gpsPointCount: points.length,
      routeQuality,
      status: this.status,
      heading: currentPoint?.heading ?? 0,
    };
  }

  private computeGpsSpeed(point: ProcessedPoint | null): number {
    if (!point) return 0;
    const pts = this.route.getPoints();
    if (pts.length < 2) return 0;
    const prev = pts[pts.length - 2];
    const dist = point.distanceFromStart - prev.distanceFromStart;
    const dt = (point.ts - prev.ts) / 1000;
    if (dt <= 0) return 0;
    return dist / dt;
  }

  private emitStats() {
    this.lastUpdateTs = Date.now();
    if (this.statsCallback) this.statsCallback(this.computeStats());
  }

  private async releaseWakeLock() {
    if (this.wakeLock) { try { await this.wakeLock.release(); } catch {} this.wakeLock = null; }
  }
}
